import { useState, useEffect, useCallback } from "react";
import { createRoot } from "react-dom/client";
import "tldraw/tldraw.css";
import { AuthProvider, useAuth } from "./AuthContext";
import Login from "./Login";
import Admin from "./Admin";
import Profile from "./Profile";
import ExportPage from "./ExportPage";
import App from "./App";

type View = "app" | "admin" | "profile" | "export";

function getViewFromPath(): View {
  const path = window.location.pathname;
  if (path === "/profile") return "profile";
  if (path === "/admin") return "admin";
  if (path === "/export") return "export";
  return "app";
}

function Router() {
  const { user, loading } = useAuth();
  const [view, setView] = useState<View>(getViewFromPath);

  const navigate = useCallback((newView: View) => {
    const path = newView === "app" ? "/" : `/${newView}`;
    window.history.pushState({}, "", path);
    setView(newView);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setView(getViewFromPath());
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#1a1a2e",
          color: "#fff",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  if (view === "admin" && user.is_admin === 1) {
    return <Admin onBack={() => navigate("app")} />;
  }

  if (view === "profile") {
    return <Profile onBack={() => navigate("app")} />;
  }

  if (view === "export") {
    return <ExportPage onBack={() => navigate("profile")} />;
  }

  return (
    <App
      onAdminClick={() => navigate("admin")}
      onProfileClick={() => navigate("profile")}
    />
  );
}

createRoot(document.getElementById("root")!).render(
  <AuthProvider>
    <Router />
  </AuthProvider>
);
